export enum ListFormattingType {
	Dot = 'dot',
	Dash = 'dash',
	ArabicNumbers = 'arabic_numbers',
	RomanNumerals = 'roman_numerals',
	RomanNumeralsCapital = 'roman_numerals_capital',
	Alphabet = 'alphabet',
	AlphabetCapital = 'alphabet_capital',
};

export type UnorderedListFormattingType =
		| typeof ListFormattingType.Dash
		| typeof ListFormattingType.Dot;

export type OrderedListFormattingType =
		| typeof ListFormattingType.Alphabet
		| typeof ListFormattingType.AlphabetCapital
		| typeof ListFormattingType.ArabicNumbers
		| typeof ListFormattingType.RomanNumerals
		| typeof ListFormattingType.RomanNumeralsCapital;

export enum BlockVariant {
	HtmlField = 'html_field',
	Text = 'text',
	Spreadsheet = 'spreadsheet',
	Explanation = 'explanation',
	Recommendation = 'recommendation',
}

export enum SearchResultType {
	ArticleTitle = 'article_title',
	BlockTitle = 'block_title',
	BlockTextContent = 'block_text_content',
}

export enum DocumentItemType {
	Cla = 'cla',
	Section = 'section',
	Article = 'article',
	Block = 'block',
}

export enum RenvoiMode {
	Normal = 'normal',
	Strikethrough = 'strikethrough',
	Removed = 'removed',
}

export type SearchOriginalItemsParams = {
	cla_slug: string;
	article?: boolean;
	search: string;
	section_id?: number;
}

export type SearchQuestionsParams = {
	cla_id: string;
	article?: boolean;
	search: string;
	limit?: number;
}

export type SearchOriginalResult = {
	id: number;
	title_draft: string;
	numbering_draft?: string;
	text_content_draft?: string;
	list_formatting_draft?: string;
	variant_draft?: string;
	is_important_draft?: string;
	is_renvoi_draft?: string;
	blocks_draft?: [{
		id: number;
		title_draft: string;
		text_content_draft: string;
	}];
}

export type MappedSearchOriginalResult = SearchOriginalResult & {
	stripped_title?: string;
}

export type QuestionLinkedItem = {
	title: string;
	title_draft: string;
	numbering?: string;
	numbering_draft?: string;
	slug?: string;
}

export type SearchQuestionResult = {
	id: number;
	question: string;
	answer: string;
	linked_articles: QuestionLinkedItem[],
	linked_blocks: QuestionLinkedItem[],
}

export type MappedQuestionLinkedItem = QuestionLinkedItem & {
	stripped_title?: string;
	stripped_title_draft?: string;
}

export type MappedQuestionResult = SearchQuestionResult & {
	linked_articles: MappedQuestionLinkedItem[],
	linked_blocks: MappedQuestionLinkedItem[],
}

export type CreateNoteData = {
	title: string,
	text?: string,
	created_by?: number,
	block?: number,
	article?: number,
}

export type UpdateNoteData = CreateNoteData

export type NotesParams = {
	cla_id?: string,
	limit?: number,
}

export type GetFilesParams = {
	section__in?: string,
	block__in?: string,
	article__in?: string,
	article?: number,
	block?: number,
	section?: number,
}


export interface CompareVersionsParams {
	sourceSlug: string;
	targetSlug: string;
	sourceDraft?: boolean;
	targetDraft?: boolean;
}

export interface ComparisonChangeDetails {
	[fieldName: string]: {
		from: any;
		to: any;
		change_type?: ChangeType;
	};
}

export enum ChangeType {
	Added = 'added',
	Deleted = 'deleted',
	Modified = 'modified',
}

export type GroupParent = {
  id: number | string;
  title: string;
  entity_type: string;
};

export interface NumberingItem {
	entity_type: 'block' | 'article' | 'section';
	id: number;
	label: string | null;
}

export interface ComparisonChange {
	entity_type: 'block' | 'article' | 'section';
	change_type: ChangeType;
	version_reference_id: string;
	entity_id_source: number | null;
	entity_id_candidate: number | null;
	text_source: string;
	text_candidate: string;
	title_source: string;
	title_candidate: string;
	subtitle_source?: string;
	subtitle_candidate?: string;
	section_name_source: string;
	section_name_candidate: string;
	article_number_full_source: string;
	article_number_full_candidate: string;
	article_block_suffix_source: string;
	article_block_suffix_candidate: string;
	block_numbering_source: NumberingItem[] | null | [];
	block_numbering_candidate: NumberingItem[] | null | [];
	article_numbering_source: NumberingItem[] | null | [];
	article_numbering_candidate: NumberingItem[] | null | [];
	is_renvoi_change: boolean;
	is_important_change: boolean;
	slug_source: string;
	slug_candidate: string;
	order_source: number | null;
	order_candidate: number | null;
	is_before_article_source?: boolean;
	is_before_article_candidate?: boolean;
	change_details?: ComparisonChangeDetails;
	grouping_parents: GroupParent[];
}

export interface Numbering {
	[id: string]: {
		id: number;
		entity_type: 'block' | 'article';
		article_numbering: { label: string | number | null; id: number; entity_type: 'article' }[];
		block_numbering: { label: string | number | null; id: number; entity_type: 'block' }[];
		title_entity: Record<string, unknown>;
	};
}

export interface CompareVersionsResponse {
	source_cla: string;
	target_cla: string;
	source_slug: string;
	target_slug: string;
	source_draft: boolean;
	target_draft: boolean;
	total_changes: number;
	changes: ComparisonChange[];
	source_numbering: Numbering;
	target_numbering: Numbering;
}


export interface ChangeDisplayInfo {
	icon: React.ReactNode;
	color: string;
	label: string;
	description: string;
}

export interface GroupedChanges {
	[sectionName: string]: ComparisonChange[];
}

export interface VersionInfo {
	slug: string;
	title: string;
	isDraft: boolean;
	displayName: string;
}

export const ChangeTypeInfo: Record<ChangeType, ChangeDisplayInfo> = {
  [ChangeType.Added]: {
    icon: 'Add',
    color: '#4caf50',
    label: 'Added',
    description: 'New content was added',
  },
  [ChangeType.Deleted]: {
    icon: 'Remove',
    color: '#f44336',
    label: 'Deleted',
    description: 'Content was removed',
  },
  [ChangeType.Modified]: {
    icon: 'Edit',
    color: '#ff9800',
    label: 'Modified',
    description: 'Content was changed',
  },
};

export const getChangeDisplayInfo = (changeType: ChangeType): ChangeDisplayInfo => {
  return ChangeTypeInfo[changeType];
};

export const hasOrderChange = (change: ComparisonChange): boolean => {
  return change.order_source !== change.order_candidate;
};

export const hasContentChange = (change: ComparisonChange): boolean => {
  return change.text_source !== change.text_candidate ||
			change.title_source !== change.title_candidate;
};

export const isStructuralChange = (change: ComparisonChange): boolean => {
  return change.change_type === ChangeType.Added ||
			change.change_type === ChangeType.Deleted;
};

export const getChangeDescription = (change: ComparisonChange): string => {
  const info = getChangeDisplayInfo(change.change_type);

  if (hasOrderChange(change)) {
    return `${info.description} (order changed: ${change.order_source} → ${change.order_candidate})`;
  }

  return info.description;
};

export const groupChangesBySection = (changes: ComparisonChange[]): GroupedChanges => {
  return changes.reduce((grouped, change) => {
    const sectionName = change.section_name_candidate || change.section_name_source || 'Other';
    if (!grouped[sectionName]) {
      grouped[sectionName] = [];
    }
    grouped[sectionName].push(change);
    return grouped;
  }, {} as GroupedChanges);
};

export const createVersionDisplayInfo = (
  slug: string,
  title: string,
  isDraft: boolean,
): VersionInfo => {
  return {
    slug,
    title,
    isDraft,
    displayName: `${title} (${isDraft ? 'draft' : 'published'})`,
  };
};

export const CHANGE_COLORS = {
  ADDED: '#4caf50',
  DELETED: '#f44336',
  MODIFIED: '#ff9800',
} as const;

export const PANEL_SETTINGS = {
  COLLAPSED_WIDTH: 60,
  EXPANDED_WIDTH: 400,
  HOVER_DELAY: 1000,
  HIGHLIGHT_DURATION: 3000,
} as const;
